import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { GaService } from '../../services/ga/ga';
import { ONBOARDING_FLOW } from '../../services/ga/onboarding.analytics';

export type CreatorStorePlatform = 'ios' | 'android' | 'none';
export type CreatorStoreId = 'ios' | 'android';

export interface CreatorStoreLink {
  id: CreatorStoreId;
  href: string;
  image: string;
  ariaLabel: string;
  recommended: boolean;
}

const APP_STORE_URL = 'https://apps.apple.com/gb/app/shout-by-unitaskr/id6466427295';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.unidosh.shout';

export function detectCreatorPlatform(userAgent: string): CreatorStorePlatform {
  if (/iPhone|iPad|iPod/i.test(userAgent)) {
    return 'ios';
  }
  if (/Android/i.test(userAgent)) {
    return 'android';
  }
  return 'none';
}

export function creatorQueryParams(params: Record<string, unknown>): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(params)) {
    const serialized = Array.isArray(value) ? value[0] : value;
    if (typeof serialized === 'string' && serialized !== '') {
      result[key] = serialized;
    }
  }
  return result;
}

export function creatorStoreLinks(platform: CreatorStorePlatform, query: string): CreatorStoreLink[] {
  const apple: CreatorStoreLink = {
    id: 'ios',
    href: withStoreQuery(APP_STORE_URL, query),
    image: '/assets/app-download/English-Apple.svg',
    ariaLabel: 'Download on the App Store',
    recommended: platform === 'ios',
  };
  const google: CreatorStoreLink = {
    id: 'android',
    href: withStoreQuery(PLAY_STORE_URL, query),
    image: '/assets/app-download/English-Play.svg',
    ariaLabel: 'Get it on Google Play',
    recommended: platform === 'android',
  };
  return platform === 'android' ? [google, apple] : [apple, google];
}

function withStoreQuery(url: string, query: string): string {
  if (!query) {
    return url;
  }
  return `${url}${url.includes('?') ? '&' : '?'}${query}`;
}

@Component({
  selector: 'app-onboarding-creator',
  imports: [],
  templateUrl: './onboarding-creator.html',
  styleUrl: './onboarding-creator.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OnboardingCreator implements OnInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly ga = inject(GaService);

  readonly perks = [
    'Briefs from real brands, matched to you',
    'Create on your phone, submit in the app',
    'Get paid when your content is approved',
  ];

  readonly platform = signal(detectCreatorPlatform(typeof navigator === 'undefined' ? '' : navigator.userAgent));

  readonly stores = computed(() => {
    const query = new URLSearchParams(creatorQueryParams(this.route.snapshot.queryParams)).toString();
    return creatorStoreLinks(this.platform(), query);
  });

  ngOnInit(): void {
    this.ga.trackOnboardingEvent('creator_app_page_view', {
      flow: ONBOARDING_FLOW.creator,
      platform: this.platform(),
    });
  }

  goBack(): void {
    this.ga.trackOnboardingEvent('back_click', { flow: ONBOARDING_FLOW.creator });
    void this.router.navigate(['/onboarding-select']);
  }

  onStoreClick(store: CreatorStoreLink): void {
    this.ga.trackOnboardingEvent('store_click', {
      flow: ONBOARDING_FLOW.creator,
      store: store.id,
      recommended: store.recommended,
    });
  }

  onBrandAgencyClick(event: Event): void {
    event.preventDefault();
    this.ga.trackOnboardingEvent('brand_agency_link_click', { flow: ONBOARDING_FLOW.creator });
    void this.router.navigate(['/onboarding-select']);
  }
}
